import { derived, get, writable } from 'svelte/store';

import { GroupsService } from '@client/services/groups';
import { VirtualBackgroundService } from '@client/services/virtual-background';
import { VirtualBackgroundRoutingService } from '@client/services/virtual-background-routing';

import { deepCopyArray } from '@common/utils/arrays';
import { generatePastelColors } from '@common/utils/colors';

/**
 * @typedef {Object} Route
 * @property {string} _id
 * @property {string} blockname
 * @property {number} priority
 * @property {boolean} status
 * @property {Array} sendergroups
 * @property {Array} selectedGroups
 * @property {string} [color]
 * @property {boolean} [editing]
 */

// State stores
export const routes = writable([]);
export const priority = writable([]);
export const reordering = writable(false);
export const dropIndex = writable(null);
export const startIndex = writable(null);
export const isSaving = writable(false);

export const groups = writable([]);
export const senderGroups = writable([]);
export const groupOptions = writable([]);
export const backgroundOptions = writable([]);

export const startPosition = writable(null);
export const targetPosition = writable(null);

export const initialized = writable(false);
export const loading = writable(false);
export const selectedRoute = writable(null);

// Derived stores
export const hasRouting = derived(
  [initialized, routes],
  ([$initialized, $routes]) => $initialized && $routes.length > 0
);

export const disableManualMove = derived(
  [startPosition, targetPosition, priority],
  ([$startPosition, $targetPosition, $priority]) => {
    // Ensure priority exists and is an array
    if (!Array.isArray($priority)) return true;

    const start = Number($startPosition);
    const target = Number($targetPosition);

    return !start || !target || start === target || target > $priority.length || start < 1;
  }
);

/**
 * Updates priority positions by moving an item from start to target index
 * @param {Route[]} list
 * @param {number} start
 * @param {number} target
 * @returns {Route[]}
 */
export function updatePriorityPositions(list, start, target) {
  if (start < target) {
    list.splice(target + 1, 0, list[start]);
    list.splice(start, 1);
  } else {
    if (target === -1) {
      list.splice(start - 1, 1);
    } else {
      list.splice(target, 0, list[start]);
      list.splice(start + 1, 1);
    }
  }

  list.forEach((item, index) => {
    item.priority = index + 1;
  });

  return list;
}

/**
 * Update priority based on source
 * @param {'routes' | 'priority'} which
 */
function updatePriority(which) {
  const currentRoutes = get(routes);
  const currentPriority = get(priority);

  const newPriority = deepCopyArray(which === 'routes' ? currentRoutes : currentPriority);
  priority.set(newPriority || []);

  if (which !== 'routes') {
    routes.set(deepCopyArray(newPriority || []));
  }
}

/**
 * Load routes data from the service
 * @returns {Promise<Route[]>}
 */
export async function loadRoutes() {
  loading.set(true);

  try {
    const response = await VirtualBackgroundRoutingService.getAll();
    const colors = generatePastelColors(response.length);

    let currentGroups = get(groups);
    if (!currentGroups.length) {
      currentGroups = await GroupsService.getAll();
      groups.set(currentGroups);
    }

    const routesData = response.map((signature, index) => {
      const routeSendergroups = currentGroups.filter(
        ({ groupname }) => signature.sendergroups.filter((group) => groupname === group.groupname).length
      );

      return {
        ...signature,
        priority: index + 1,
        color: colors[index],
        selectedGroups: routeSendergroups.map(({ groupname }) => ({ label: groupname, value: groupname })),
        sendergroups: routeSendergroups
      };
    });

    const options = currentGroups.map(({ groupname }) => ({ label: groupname, value: groupname }));
    groupOptions.set(options);

    routes.set(routesData);
    priority.set(deepCopyArray(routesData) || []);
    senderGroups.set(currentGroups);
    initialized.set(true);

    return routesData;
  } catch (error) {
    console.error('Error loading routes:', error);
    throw error;
  } finally {
    loading.set(false);
  }
}

/**
 * Load virtual backgrounds for dropdown options
 * @returns {Promise<void>}
 */
export async function loadBackgrounds() {
  try {
    const bizCards = await VirtualBackgroundService.getAll();
    const cards = Array.isArray(bizCards) ? bizCards : [];
    const options = cards.map(({ rulename }) => rulename).sort((a, b) => a.localeCompare(b));
    backgroundOptions.set(options);
  } catch (error) {
    console.error('Error loading virtual backgrounds:', error);
    throw error;
  }
}

/**
 * Cancel reordering and restore original order
 */
export function cancelReorder() {
  reordering.set(false);
  updatePriority('routes');
}

/**
 * Save reordered routes
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function saveReorder() {
  isSaving.set(true);

  try {
    const currentPriority = get(priority);
    const ids = currentPriority.map(({ _id }) => _id);
    const response = await VirtualBackgroundRoutingService.saveRouteOrder(ids);

    if (response.status >= 400) {
      return {
        success: false,
        error: 'Failed to update routing priority'
      };
    }

    updatePriority('priority');
    reordering.set(false);

    return { success: true };
  } catch (error) {
    console.error('Error saving route order:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    };
  } finally {
    isSaving.set(false);
  }
}

/**
 * Update positions manually using start and target position inputs
 */
export function updatePositions() {
  const start = get(startPosition);
  const target = get(targetPosition);
  const currentPriority = get(priority);

  const updated = updatePriorityPositions(currentPriority, Number(start) - 1, Number(target) - 1);
  priority.set(updated);

  startPosition.set(null);
  targetPosition.set(null);
}

/**
 * Add a new route
 */
export function addRoute() {
  selectedRoute.set({
    blockname: '',
    priority: 0,
    editing: true,
    status: false,
    sendergroups: [],
    selectedGroups: []
  });
}

/**
 * Delete route and update priorities
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deleteRoute() {
  try {
    const currentPriority = get(priority);

    currentPriority.forEach((item, index) => {
      item.priority = index + 1;
    });

    const ids = currentPriority.map(({ _id }) => _id);
    const response = await VirtualBackgroundRoutingService.saveRouteOrder(ids);

    if (response.status >= 400) {
      return {
        success: false,
        error: 'Failed to delete routing priority'
      };
    }

    // Reload routes after deletion
    await loadRoutes();

    return { success: true };
  } catch (error) {
    console.error('Error deleting route:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    };
  }
}

/**
 * Start reordering mode
 */
export function startReordering() {
  reordering.set(true);
}

/**
 * Reset all stores to initial state
 */
export function resetStore() {
  routes.set([]);
  priority.set([]);
  reordering.set(false);
  dropIndex.set(null);
  startIndex.set(null);
  isSaving.set(false);
  groups.set([]);
  senderGroups.set([]);
  groupOptions.set([]);
  backgroundOptions.set([]);
  startPosition.set(null);
  targetPosition.set(null);
  initialized.set(false);
  loading.set(false);
  selectedRoute.set(null);
}
